/**
 * ArbScanner - Cross-venue arbitrage detection panel.
 *
 * Adapts GameRow[] from marketStore into ConsolidatedGameBooks[]
 * for detectArbs(), then renders fee-adjusted arb cards sorted
 * by net spread descending.
 */

import { useMemo } from 'react';
import { detectArbs } from '@/lib/market/arbitrage';
import type { ArbOpportunity } from '@/lib/market/arbitrage';
import type { ConsolidatedGameBooks } from '@/types';
import type { GameRow } from '@/views/ValueDashboard/types';

// ---------------------------------------------------------------------------
// Adapter: GameRow[] -> ConsolidatedGameBooks[]
//
// GameRow has flat venue books (game.kalshi.away),
// ConsolidatedGameBooks nests them under .markets (game.kalshi.markets.away).
// ---------------------------------------------------------------------------

function adaptGames(games: GameRow[]): ConsolidatedGameBooks[] {
  return games.map((g) => ({
    key: g.key,
    date: g.date,
    awayCode: g.awayCode,
    homeCode: g.homeCode,
    kalshi: g.kalshi
      ? {
          venue: 'kalshi' as const,
          eventId: '',
          awayCode: g.awayCode,
          homeCode: g.homeCode,
          markets: {
            away: {
              marketTicker: g.kalshi.away.marketTicker,
              yes: g.kalshi.away.yes,
              no: g.kalshi.away.no,
            },
            home: {
              marketTicker: g.kalshi.home.marketTicker,
              yes: g.kalshi.home.yes,
              no: g.kalshi.home.no,
            },
          },
        }
      : null,
    polymarket: g.polymarket
      ? {
          venue: 'polymarket' as const,
          eventId: '',
          awayCode: g.awayCode,
          homeCode: g.homeCode,
          markets: {
            away: {
              marketTicker: g.polymarket.away.marketTicker,
              yes: g.polymarket.away.yes,
              no: g.polymarket.away.no,
            },
            home: {
              marketTicker: g.polymarket.home.marketTicker,
              yes: g.polymarket.home.yes,
              no: g.polymarket.home.no,
            },
          },
        }
      : null,
  }));
}

// ---------------------------------------------------------------------------
// Direction label
// ---------------------------------------------------------------------------

function directionLabel(arb: ArbOpportunity): string {
  const buyTag = arb.buyVenue === 'kalshi' ? 'K' : 'P';
  const sellTag = arb.sellVenue === 'kalshi' ? 'K' : 'P';
  return `${buyTag} \u2192 ${sellTag}`;
}

// ---------------------------------------------------------------------------
// Card background color based on net spread quality
// ---------------------------------------------------------------------------

function cardBg(netSpreadCents: number): string {
  if (netSpreadCents > 2) return 'bg-green-500/10 border-green-500/30';
  if (netSpreadCents >= 1) return 'bg-yellow-500/10 border-yellow-500/30';
  return 'bg-card border-border';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ArbScannerProps {
  games: GameRow[];
}

export function ArbScanner({ games }: ArbScannerProps) {
  const arbs = useMemo(() => {
    const adapted = adaptGames(games);
    return detectArbs(adapted);
  }, [games]);

  if (arbs.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <svg
          className="h-8 w-8 text-green-500/60"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <span className="text-muted-foreground text-sm">Markets Aligned</span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-1.5 overflow-y-auto p-2">
      {arbs.map((arb, i) => (
        <div
          key={`${arb.gameKey}-${arb.side}-${i}`}
          className={`rounded-md border px-3 py-2 ${cardBg(arb.netSpreadCents)}`}
        >
          {/* Header row: game label + direction */}
          <div className="flex items-center justify-between">
            <span className="text-foreground text-xs font-semibold">
              {arb.awayCode} @ {arb.homeCode}
              <span className="text-muted-foreground ml-1.5 font-normal">
                ({arb.side})
              </span>
            </span>
            <span className="bg-primary/20 text-primary rounded px-1.5 py-0.5 text-[10px] font-bold">
              {directionLabel(arb)}
            </span>
          </div>

          {/* Metrics row */}
          <div className="mt-1.5 grid grid-cols-4 gap-2 font-mono text-[10px]">
            <div>
              <span className="text-muted-foreground block">Raw</span>
              <span className="text-foreground">{arb.rawSpreadCents.toFixed(1)}&cent;</span>
            </div>
            <div>
              <span className="text-muted-foreground block">Net</span>
              <span
                className={
                  arb.netSpreadCents > 2
                    ? 'font-semibold text-green-400'
                    : arb.netSpreadCents >= 1
                      ? 'font-semibold text-yellow-400'
                      : 'text-foreground'
                }
              >
                {arb.netSpreadCents.toFixed(1)}&cent;
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block">$/1K</span>
              <span className="text-green-400">${arb.profitPer1000.toFixed(2)}</span>
            </div>
            <div>
              <span className="text-muted-foreground block">Size</span>
              <span className="text-foreground">{arb.availableSize.toLocaleString()}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
